import Skeleton from '@atlaskit/skeleton';
import { bridgeEventEmitter } from '@databases/rovo-bridge';
import React, {
  lazy,
  type FunctionComponent,
  Suspense,
  useEffect,
  useState
} from 'react';

import { OpenRovoSidePanelCommand } from '@canvas/databases-bridge-api/commands';
import { CONFLUENCE_AFM_CLIENT_ID } from '@canvas/databases-bridge-api/types';
import { dynamicImport } from '@canvas/dynamic-import';
import { client } from '@canvas/rovo-content-bridge-api';

import { useShouldShowDatabaseBlankObjectExperience } from './useShouldShowDatabaseBlankObjectExperience.ts';

const DatabaseBlankObjectExperience = lazy(() =>
  dynamicImport(() => import('./DatabaseBlankObjectExperience.tsx')).then(
    ({ DatabaseBlankObjectExperience }) => ({ default: DatabaseBlankObjectExperience })
  )
);

const DatabaseBoeThinkingBar = lazy(() =>
  dynamicImport(() => import('./DatabaseBoeThinkingBar.tsx')).then(
    ({ DatabaseBoeThinkingBar }) => ({ default: DatabaseBoeThinkingBar })
  )
);

const DatabaseBoeThinkingBarFallback = () => (
  <Skeleton isShimmering width="100%" height="40px" />
);

interface DatabaseBlankObjectExperienceFooterProps {
  contentId: string;
  isDbAiFirstCreationEnabled: boolean;
  isDatabaseLoaded: boolean;
  isEmptyDatabase: boolean;
  isInAIFCStagingAreaView: boolean;
}

export const DatabaseBlankObjectExperienceFooter: FunctionComponent<
  DatabaseBlankObjectExperienceFooterProps
> = ({
  contentId,
  isDbAiFirstCreationEnabled,
  isDatabaseLoaded,
  isEmptyDatabase,
  isInAIFCStagingAreaView
}) => {
  const [isRovoThinking, setIsRovoThinking] = useState<boolean>(false);

  const shouldShowBlankObjectExperience = useShouldShowDatabaseBlankObjectExperience({
    isEmptyDatabase,
    isDbAiFirstCreationEnabled,
    isDatabaseLoaded,
    isInAIFCStagingAreaView
  });

  const handleSubmit = () => {
    setIsRovoThinking(true);
  };

  const handleThinkingDismiss = () => {
    setIsRovoThinking(false);
  };

  const handleThinkingComplete = () => {
    bridgeEventEmitter.sendEvent(OpenRovoSidePanelCommand, {
      senderId: client(`confluence-database-${contentId}`),
      receiverId: CONFLUENCE_AFM_CLIENT_ID
    });
    setIsRovoThinking(false);
  };

  // Reset local thinking state if this footer is reused for a different database.
  useEffect(() => {
    setIsRovoThinking(false);
  }, [contentId]);

  if (isRovoThinking) {
    return (
      <Suspense fallback={<DatabaseBoeThinkingBarFallback />}>
        <DatabaseBoeThinkingBar
          contentId={contentId}
          onDismiss={handleThinkingDismiss}
          onStreamComplete={handleThinkingComplete}
          placement="inline"
        />
      </Suspense>
    );
  }

  if (shouldShowBlankObjectExperience) {
    return (
      <Suspense fallback={null}>
        <DatabaseBlankObjectExperience contentId={contentId} onSubmit={handleSubmit} />
      </Suspense>
    );
  }

  return null;
};
